import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  FaBuilding,
  FaChevronDown,
  FaChevronRight,
  FaFileExcel,
  FaMapMarkerAlt,
} from 'react-icons/fa';
import Loading from '~/Components/Loading';
import { salvarEmExcel } from '~/Utils/Helpers';
import { buscarDetalhamentoFcDw } from '~/Services/Service';
import CelulaComTooltip from './CelulaComTooltip';
import TabelaFluxoCaixa from './TabelaFluxoCaixa';
import {
  formatarMoeda,
  getMensagemErro,
  normalizarLista,
} from '../utils';

const normalizarTexto = valor => (valor != null ? `${valor}`.trim() : '');

const escaparRegex = valor => valor.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

const compararTexto = (a, b) =>
  normalizarTexto(a).localeCompare(normalizarTexto(b), 'pt-BR', {
    numeric: true,
    sensitivity: 'base',
  });

const formatarCodigoGe = ge => {
  const codigo = normalizarTexto(ge);
  if (/^\d+$/.test(codigo)) return `000${codigo}`.slice(-3);
  return codigo;
};

const getOrdemGe = ge => {
  const codigo = normalizarTexto(ge);
  if (/^\d+$/.test(codigo)) return Number(codigo);

  const inicioNumerico = codigo.match(/^\d+/);
  return inicioNumerico ? Number(inicioNumerico[0]) : 999999999;
};

const removerPrefixosCodigoGe = (descricao, codigoRaw, codigoFormatado) => {
  const codigos = [codigoFormatado, codigoRaw].filter(
    (codigo, index, lista) => codigo && lista.indexOf(codigo) === index
  );
  let resultado = descricao;
  let tentativas = 0;

  while (tentativas < 4) {
    const anterior = resultado;

    codigos.forEach(codigo => {
      const regex = new RegExp(`^${escaparRegex(codigo)}\\s*[-–]?\\s*`, 'i');
      resultado = resultado.replace(regex, '').trim();
    });

    if (resultado === anterior) return resultado;
    tentativas += 1;
  }

  return resultado;
};

const getDescricaoGe = linha => {
  const codigoRaw = normalizarTexto(linha.ge);
  const codigo = formatarCodigoGe(linha.ge);
  const completo = normalizarTexto(linha.ge_completo);

  if (!codigo && !completo) return '-';
  if (!completo || completo === codigoRaw || completo === codigo) {
    return codigo || completo;
  }

  const nome = removerPrefixosCodigoGe(completo, codigoRaw, codigo);

  if (!nome) return codigo || completo;
  return codigo ? `${codigo} - ${nome}` : nome;
};

const isLinhaTotalResponse = linha => {
  const ge = normalizarTexto(linha.ge).toLowerCase();
  const descricao = normalizarTexto(linha.descricao).toLowerCase();
  return ge === 'total' || descricao === 'total';
};

const getLinhaId = linha => {
  if (linha.nivel === 0) return `ge-${linha.ge || 'sem-ge'}`;
  if (linha.nivel === 1) {
    return `emp-${linha.ge || 'sem-ge'}-${linha.empreendimento ||
      'sem-empreendimento'}`;
  }
  return `lote-${linha.ge || 'sem-ge'}-${linha.empreendimento ||
    'sem-empreendimento'}-${linha.lote || 'sem-lote'}`;
};

const getParentId = linha => {
  if (linha.nivel === 1) return `ge-${linha.ge || 'sem-ge'}`;
  if (linha.nivel === 2) {
    return `emp-${linha.ge || 'sem-ge'}-${linha.empreendimento ||
      'sem-empreendimento'}`;
  }
  return null;
};

const encontrarValorAno = (linha, ano) => {
  const valores = normalizarLista(linha.valores_por_ano);
  return valores.find(item => item.ano === ano);
};

const normalizarLinha = (linha, anos) => {
  const normalizada = {
    ...linha,
    id: getLinhaId(linha),
    parent_id: getParentId(linha),
    ge_id: `ge-${linha.ge || 'sem-ge'}`,
    ge_ordem: getOrdemGe(linha.ge),
    ge_label: getDescricaoGe(linha),
    descricao:
      linha.nivel === 0
        ? getDescricaoGe(linha)
        : linha.nivel === 1
        ? linha.empreendimento
        : linha.lote,
  };

  anos.forEach(ano => {
    const valorAno = encontrarValorAno(linha, ano);
    normalizada[`ano_${ano}`] =
      valorAno && valorAno.valor != null ? valorAno.valor : null;
    normalizada[`cor_${ano}`] =
      valorAno && valorAno.cor ? valorAno.cor : 'neutro';
  });

  return normalizada;
};

const ordenarLinhasHierarquia = linhas =>
  [...linhas].sort((a, b) => {
    if (a.ge_ordem !== b.ge_ordem) return a.ge_ordem - b.ge_ordem;

    const geCompare = compararTexto(a.ge_label, b.ge_label);
    if (geCompare !== 0) return geCompare;

    const empreendimentoCompare = compararTexto(
      a.nivel === 0 ? '' : a.empreendimento,
      b.nivel === 0 ? '' : b.empreendimento
    );
    if (empreendimentoCompare !== 0) return empreendimentoCompare;

    if (a.nivel !== b.nivel) return a.nivel - b.nivel;

    return compararTexto(a.lote, b.lote);
  });

const marcarLinhasComFilhos = linhas => {
  const idsComFilhos = {};
  linhas.forEach(linha => {
    if (linha.parent_id) {
      idsComFilhos[linha.parent_id] = true;
    }
  });

  return linhas.map(linha => ({
    ...linha,
    pode_expandir: idsComFilhos[linha.id] === true,
  }));
};

const calcularTotal = (linhas, anos, totalResponse) => {
  const total = {
    id: 'total-analise-fluxo',
    descricao: 'Total',
    nivel: 0,
    is_total: true,
  };

  anos.forEach(ano => {
    total[`ano_${ano}`] = totalResponse
      ? Number(totalResponse[`ano_${ano}`] || 0)
      : linhas
          .filter(linha => linha.nivel === 0)
          .reduce((acc, linha) => acc + Number(linha[`ano_${ano}`] || 0), 0);
    total[`cor_${ano}`] = 'neutro';
  });

  return total;
};

const corClasse = cor => {
  if (cor === 'verde') return 'fluxo-caixa-dw-status-verde';
  if (cor === 'vermelho') return 'fluxo-caixa-dw-status-vermelho';
  if (cor === 'amarelo') return 'fluxo-caixa-dw-status-amarelo';
  return 'fluxo-caixa-dw-status-neutro';
};

const renderIconeNivel = item => {
  if (item.nivel === 0) {
    return <span className="fluxo-caixa-dw-hierarquia-badge">GE</span>;
  }
  if (item.nivel === 1) {
    return <FaBuilding className="fluxo-caixa-dw-hierarquia-icone" />;
  }
  return <FaMapMarkerAlt className="fluxo-caixa-dw-hierarquia-icone" />;
};

const AbaAnaliseFluxo = ({ filtros }) => {
  const [loading, setLoading] = useState(false);
  const [linhas, setLinhas] = useState([]);
  const [anos, setAnos] = useState([]);
  const [totalResponse, setTotalResponse] = useState(null);
  const [expandidos, setExpandidos] = useState({});

  useEffect(() => {
    let ativo = true;

    const carregarDados = async () => {
      setLoading(true);
      try {
        const response = await buscarDetalhamentoFcDw(filtros);
        if (!ativo) return;

        const anosResponse = normalizarLista(
          response && response.anos ? response.anos : []
        );
        const linhasNormalizadas = normalizarLista(
          response && response.linhas ? response.linhas : []
        ).map(linha => normalizarLinha(linha, anosResponse));
        const linhaTotalResponse = linhasNormalizadas.find(
          isLinhaTotalResponse
        );
        const linhasHierarquia = linhasNormalizadas.filter(
          linha => !isLinhaTotalResponse(linha)
        );
        const linhasResponse = marcarLinhasComFilhos(
          ordenarLinhasHierarquia(linhasHierarquia)
        );

        setAnos(anosResponse);
        setLinhas(linhasResponse);
        setTotalResponse(linhaTotalResponse || null);
        setExpandidos({});
      } catch (err) {
        if (ativo) {
          toast.error(
            getMensagemErro(err, 'Erro ao carregar Análise de Fluxo.')
          );
        }
      } finally {
        if (ativo) {
          setLoading(false);
        }
      }
    };

    carregarDados();

    return () => {
      ativo = false;
    };
  }, [filtros]);

  const linhasTabela = useMemo(() => {
    const visiveis = linhas.filter(linha => {
      if (linha.nivel === 0) return true;
      if (linha.nivel === 1) return expandidos[linha.parent_id] === true;
      if (linha.nivel === 2) {
        return (
          expandidos[linha.ge_id] === true &&
          expandidos[linha.parent_id] === true
        );
      }
      return true;
    });

    if (linhas.length > 0 || totalResponse) {
      return [...visiveis, calcularTotal(linhas, anos, totalResponse)];
    }

    return visiveis;
  }, [anos, expandidos, linhas, totalResponse]);

  const toggleLinha = useCallback(id => {
    setExpandidos(current => ({
      ...current,
      [id]: current[id] !== true,
    }));
  }, []);

  const colunas = useMemo(() => {
    const colunaHierarquia = {
      title: 'GE / Empreendimento / Lote',
      key: 'descricao',
      width: '420px',
      render: (value, item) => {
        const classeNivel =
          item.nivel === 1
            ? 'fluxo-caixa-dw-hierarquia-n1'
            : item.nivel === 2
            ? 'fluxo-caixa-dw-hierarquia-n2'
            : '';
        const podeExpandir = !item.is_total && item.pode_expandir;

        return (
          <span className={`fluxo-caixa-dw-hierarquia ${classeNivel}`}>
            {podeExpandir && (
              <Button
                className="fluxo-caixa-dw-toggle"
                aria-label={
                  expandidos[item.id] === true
                    ? 'Recolher linha'
                    : 'Expandir linha'
                }
                onClick={() => toggleLinha(item.id)}
                size="sm"
                type="button"
                variant="link"
              >
                {expandidos[item.id] === true ? (
                  <FaChevronDown />
                ) : (
                  <FaChevronRight />
                )}
              </Button>
            )}
            {!podeExpandir && !item.is_total && (
              <span className="fluxo-caixa-dw-toggle-placeholder" />
            )}
            {item.is_total ? (
              <strong>Total</strong>
            ) : (
              <>
                {renderIconeNivel(item)}
                <span className="fluxo-caixa-dw-hierarquia-texto">
                  <CelulaComTooltip id={item.id} texto={value || '-'}>
                    {value || '-'}
                  </CelulaComTooltip>
                </span>
              </>
            )}
          </span>
        );
      },
      excel: item => item.descricao,
    };

    return [
      colunaHierarquia,
      ...anos.map(ano => ({
        title: `${ano}`,
        key: `ano_${ano}`,
        align: 'right',
        render: (value, item) => {
          const classe = corClasse(item[`cor_${ano}`]);
          return (
            <span className="fluxo-caixa-dw-analise-valor">
              {!item.is_total && (
                <span className={`fluxo-caixa-dw-analise-status ${classe}`} />
              )}
              {item.is_total ? (
                <strong>{formatarMoeda(value)}</strong>
              ) : (
                formatarMoeda(value)
              )}
            </span>
          );
        },
        excel: item => formatarMoeda(item[`ano_${ano}`]),
      })),
    ];
  }, [anos, expandidos, toggleLinha]);

  const exportar = async () => {
    try {
      await salvarEmExcel(
        filtros,
        '/exportador/excel/fluxo-caixa-dw/analise-fluxo'
      );
    } catch (err) {
      toast.error(getMensagemErro(err, 'Erro ao exportar Análise de Fluxo.'));
    }
  };

  if (loading) {
    return <Loading label="Carregando Análise de Fluxo..." />;
  }

  return (
    <div className="fluxo-caixa-dw-panel fluxo-caixa-dw-panel--full fluxo-caixa-dw-panel--analise">
      <div className="fluxo-caixa-dw-panel-header">
        <h3 className="fluxo-caixa-dw-panel-title">
          <span className="fluxo-caixa-dw-title-marker" />
          Análise de Fluxo
        </h3>
        <Button
          disabled={linhasTabela.length === 0}
          onClick={exportar}
          size="sm"
          variant="outline-success"
        >
          <FaFileExcel className="mr-1" />
          Excel
        </Button>
      </div>
      <div className="fluxo-caixa-dw-panel-body fluxo-caixa-dw-panel-body--table fluxo-caixa-dw-panel-body--analise">
        <TabelaFluxoCaixa
          cellLineHeight="32px"
          cellPadding="0 8px"
          cellWhiteSpace="nowrap"
          colunas={colunas}
          dados={linhasTabela}
          fontSize="12px"
          headerBgColor="#0b4f9c"
          headerLineHeight="34px"
          headerPadding="0 8px"
          minWidth={`${Math.max(900, 420 + anos.length * 118)}px`}
          tableLayout="fixed"
        />
      </div>
    </div>
  );
};

export default AbaAnaliseFluxo;
